import React, { useEffect, useRef, useState, useCallback } from "react";
import { getMeetingPointPlaceFromAutocomplete, fetchMeetingPointAutocomplete } from "./networking";
import { useDictionary } from "../../../../utilities/hooks/UseDictionary";
import AutocompleteResultItem from "./AutocompleteResultItem";
import { v4 as uuidv4 } from "uuid"; // Import UUID generator

const AutocompleteInput = ({ setPlace, experienceId }) => {
    const dictionary = useDictionary();
    const [inputValue, setInputValue] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [showDropdown, setShowDropdown] = useState(false);
    const [sessionToken, setSessionToken] = useState(uuidv4()); // Generate session token
    const inputRef = useRef(null);
    const debounceTimeout = useRef(null); // Store debounce timeout reference

    // Debounced API request for autocomplete suggestions
    const fetchSuggestions = useCallback((query) => {
        if (query.length < 3) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }

        clearTimeout(debounceTimeout.current); // Clear previous timeout if user keeps typing

        debounceTimeout.current = setTimeout(async () => {
            try {
                const response = await fetchMeetingPointAutocomplete(query, sessionToken, experienceId);
                setSuggestions(response.data.suggestions || []);
                setShowDropdown(true);
            } catch (error) {
                console.error("Error fetching autocomplete results:", error);
                setSuggestions([]);
            }
        }, 300); // Wait 300ms after user stops typing before sending request
    }, [sessionToken]);

    // Fetch place details from backend
    const fetchPlaceDetails = async (placeId) => {
        try {
            return await getMeetingPointPlaceFromAutocomplete(placeId, sessionToken, experienceId);
        } catch (error) {
            console.error("Error fetching place details:", error);
        }
    };

    // Handle selection of a place
    const handleSelect = (placeText, placeId) => {
        setInputValue('');
        setShowDropdown(false);

        fetchPlaceDetails(placeId).then(response => {
            setPlace(response.data);
            setSessionToken(uuidv4()); // Generate a new session token after selection
        });
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (suggestions.length === 0) return;

        if (e.key === "ArrowDown") {
            setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
        } else if (e.key === "ArrowUp") {
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
        } else if (e.key === "Enter") {
            if (selectedIndex !== -1) {
                const selectedPlace = suggestions[selectedIndex];
                handleSelect(selectedPlace.placePrediction?.text?.text, selectedPlace.placePrediction?.placeId);
            }
        }
    };

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (inputRef.current && !inputRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className="autocomplete-container" ref={inputRef}>
            <div className="autocomplete-input-container">
                <div className="autocomplete-address-icon"></div> {/* Custom icon */}
                <input
                    type="text"
                    placeholder={dictionary.autocomplete_search}
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        fetchSuggestions(e.target.value); // Debounced call
                    }}
                    onKeyDown={handleKeyDown}
                    className="autocomplete-input"
                    style={{ paddingLeft: "30px" }} // Needed for group-inputs CSS class
                />
            </div>
            {showDropdown && suggestions.length > 0 && (
                <div className="autocomplete-dropdown">
                    {suggestions.map((suggestionItem, index) => (
                        <AutocompleteResultItem
                            key={index}
                            suggestionItem={suggestionItem}
                            onSelect={handleSelect}
                            isSelected={(index === selectedIndex)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default AutocompleteInput;
